import { useEffect, useState } from "react"

//Formik
import { useField } from "formik"
import FormikDropDown from "../../../../../helpers/formikInputs/FormikDropDown"
import FormikTextInput from "../../../../../helpers/formikInputs/FormikTextInput"
import FormikCheckboxInput from "../../../../../helpers/formikInputs/FormikCheckboxInput"
import FormikCheckBox from "../../../../../helpers/formikInputs/FormikCheckboxInput"

//Styles
import assessStyles from "../../../../../styles/assessments/Assessment.module.css"
import formStyles from "../../../../../styles/assessments/ManagementForm.module.css"
import elementTypes from "../Elements/elementTypes"
import ConditionalPropertiesTestInput from "./ConditionalPropertiesTestInput"

export default function ConditionalProperties({
  formikQuestionName,
  questionId, //Used in useEffect to filter list of available questions
  questionOrders,
  disabled,
}) {
  const [sourceQuestions, setSourceQuestions] = useState([])
  const [sourceQuestion, setSourceQuestion] = useState(undefined)

  const [questionField, ,] = useField(formikQuestionName)
  const [questionIdField, ,] = useField(`${formikQuestionName}.id`)
  const [, , conditionalRequiredHelpers] = useField(
    `${formikQuestionName}.conditionalRequired`
  )
  const [isConditionalField, , isConditionalHelper] = useField(
    `${formikQuestionName}.isConditional`
  )
  const [conditionalSourceField, , conditionalSourceHelpers] = useField(
    `${formikQuestionName}.conditionalSource`
  )
  const [conditionalDisplayField, , conditionalDisplayHelpers] = useField(
    `${formikQuestionName}.conditionalDisplay`
  )

  const [conditionalTypeField, , conditionalTypeHelpers] = useField(
    `${formikQuestionName}.conditionalType`
  )

  const [conditionalValueField, , conditionalValueHelpers] = useField(
    `${formikQuestionName}.conditionalValue`
  )

  //This is used for runtime tracking of multiple selection values, not persisted to the API.
  const [conditionalValueMultiField, , conditionalValueMultiHelpers] = useField(
    `${formikQuestionName}.conditionalValueMulti`
  )

  const conditionalTypesIn = [
    { label: "[IN] In list", value: "IN" },
    { label: "[NOT IN] Not in list", value: "NOT IN" },
  ]

  let conditionalTypes = [
    { label: "[==] Equals", value: "==" },
    { label: "[!=] Not equal to", value: "!=" },
    ...conditionalTypesIn,
  ]

  //add additional conditional types based on source question type
  if (
    [
      elementTypes.numberElementId,
      elementTypes.dateElementId,
      elementTypes.timeElementId,
    ].includes(sourceQuestion?.type)
  ) {
    conditionalTypes = [
      ...conditionalTypes,
      { label: "[<] Less than", value: "<" },
      { label: "[<=] Less than or equal to", value: "<=" },
      { label: "[>] More than", value: ">" },
      { label: "[>=] More than or equal to", value: ">=" },
    ]
  }

  const conditionalDisplayAlwaysOn =
    elementTypes.propertiesConfig.conditionalDisplayAlwaysOn.includes(
      questionField.value?.type
    )

  useEffect(() => {
    let questions = [{ label: "No source linked", value: undefined }]

    const addQuestion = (sourceQuestion, pathName) => {
      if (
        sourceQuestion.id !== questionIdField.value &&
        !elementTypes.isMatrixType(sourceQuestion.type)
      ) {
        let label = sourceQuestion.text

        if (pathName) {
          label = `${pathName} >> ${label}`
        }

        questions.push({
          label: label,
          value: sourceQuestion.id,
          type: sourceQuestion.type,
          options: sourceQuestion.options,
        })
      }
    }

    questionOrders?.forEach((order) => {
      if (!order.isDeleted) {
        if (order.question) {
          addQuestion(order.question)
        } else if (order.questionGroup) {
          order.questionGroup.questions.forEach((question) => {
            if (question.isActive) {
              addQuestion(question, order.questionGroup.name)
            }
          })
        }
      }
    })

    setSourceQuestions(questions)

    if (conditionalSourceField.value && !isConditionalField.value) {
      isConditionalHelper.setValue(true)
    }

    if (conditionalSourceField.value) {
      setSourceQuestion(
        questions.find(
          (question) => question.value === conditionalSourceField.value
        )
      )
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionIdField.value])

  //matrix types are not part of the conditional questions at this stage.
  if (questionField && elementTypes.isMatrixType(questionField.value?.type)) {
    return null
  }

  return (
    <>
      <div className={assessStyles.switchControl}>
        <FormikCheckboxInput
          name={`${formikQuestionName}.isConditional`}
          label={"Conditional question"}
          helpText={"Does this quesiton have a dependency on another question?"}
          useSwitch={true}
          onChangeCallback={(value) => {
            if (value === true) {
              if (
                conditionalDisplayAlwaysOn &&
                !conditionalDisplayField.value &&
                value
              ) {
                conditionalDisplayHelpers.setValue(true)
              }

              if (questionField.value?.conditionalSource !== undefined) {
                conditionalRequiredHelpers.setValue(true)
              }
            } else {
              conditionalSourceHelpers.setValue(undefined)
              conditionalRequiredHelpers.setValue(false)
            }
          }}
          disabled={disabled}
        />
      </div>

      {isConditionalField.value ? (
        <>
          <FormikDropDown
            label={"Source question"}
            name={`${formikQuestionName}.conditionalSource`}
            helpText={
              "A linked question that will indicate if this question requires an answer"
            }
            placeHolderValue={"No source linked"}
            options={sourceQuestions}
            optionLabel={"label"}
            optionValue={"value"}
            onChangeCallback={(value) => {
              conditionalRequiredHelpers.setValue(value !== undefined)
              if (value) {
                const newSourceQuestion = sourceQuestions.find(
                  (question) => question.value === value
                )

                if (newSourceQuestion.options || sourceQuestion?.options) {
                  conditionalValueHelpers.setValue(undefined)
                  conditionalValueMultiHelpers.setValue([])
                }

                setSourceQuestion(newSourceQuestion)
              } else {
                setSourceQuestion(undefined)
              }
            }}
            disabled={disabled}
          />

          <FormikDropDown
            label={"Conditional type"}
            name={`${formikQuestionName}.conditionalType`}
            helpText={
              "The conditional logic to be applied on the source question answer"
            }
            onChangeCallback={(value) => {
              if (
                conditionalTypesIn.find(
                  (type) => type.value === conditionalTypeField.value
                ) &&
                !conditionalTypesIn.find((type) => type.value === value)
              ) {
                //If we are changing from an IN/NOT IN to another type clear the multi values
                conditionalValueHelpers.setValue(
                  !!conditionalValueMultiField.value &&
                    conditionalValueMultiField.value.length > 0
                    ? conditionalValueMultiField.value[0]
                    : undefined
                )

                conditionalValueMultiHelpers.setValue([])
              } else if (
                !conditionalTypesIn.find(
                  (type) => type.value === conditionalTypeField.value
                ) &&
                conditionalTypesIn.find((type) => type.value === value) &&
                conditionalValueField.value
              ) {
                //If we are changing from another type to IN/NOT INT prep the multi values
                conditionalValueMultiHelpers.setValue([
                  conditionalValueField.value,
                ])
              }
            }}
            options={conditionalTypes}
            required={questionField.value?.conditionalSource}
            disabled={!questionField.value?.conditionalSource || disabled}
          />
          <ConditionalPropertiesTestInput
            question={questionField.value}
            formikQuestionName={formikQuestionName}
            sourceQuestion={sourceQuestion}
            disabled={disabled}
          />

          <FormikCheckBox
            label={"Conditional display"}
            name={`${formikQuestionName}.conditionalDisplay`}
            helpText={
              conditionalDisplayAlwaysOn
                ? "This input type will always be displayed conditionally if it is linked to a conditional source"
                : "When selected, this question will only be displayed if the conditional test is validated to true"
            }
            useSwitch={true}
            className={`${formStyles.switchControl}`}
            disabled={
              !questionField.value?.conditionalSource ||
              disabled ||
              elementTypes.propertiesConfig.conditionalDisplayAlwaysOn.includes(
                questionField.value?.type
              )
            }
          />
        </>
      ) : null}
    </>
  )
}
